import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import workAnalyzerService from '../services/workAnalyzerService';
import InspectionEvaluationIssueDisplay from '../components/InspectionEvaluationComponents/InspectionEvaluationIssueDisplay';
import { InspectionContext, InspectionEvaluationIssue, InspectionEvaluationResponse } from '../models/inspectionEvaluationIssue';
import InspectionEvaluationSummaryDisplay from '../components/InspectionEvaluationComponents/InspectionEvaluationSummaryDisplay';
import InspectionEvaluationApplianceDisplay from '../components/InspectionEvaluationComponents/InspectionEvaluationApplianceDisplay';

const InspectionEvaluation: React.FC = () => {
  const { inspectionId } = useParams<{ inspectionId?: string }>();
  const navigate = useNavigate();
  const [inspectionEvaluation, setInspectionEvaluation] = useState<InspectionEvaluationResponse | null>(null);
  const [totalLowCost, setTotalLowCost] = useState<number>(0);
  const [totalHighCost, setTotalHighCost] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<{ [key: string]: any } | null>(null);
  const [solutionLoading, setSolutionLoading] = useState(false);

  useEffect(() => {
    if (inspectionId) {
      const fetchInspection = async () => {
        setLoading(true);
        try {
          const inspectionData = await workAnalyzerService.getInspectionEvaluation(inspectionId);
          setInspectionEvaluation(inspectionData);
        } catch (error) {
          console.error("Failed to fetch inspection:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchInspection();
    }
  }, [inspectionId]);

  useEffect(() => {
    if (inspectionEvaluation && inspectionEvaluation.issues) {
      const visibleIssues = inspectionEvaluation.issues.filter(issue => !issue.hidden);
      setTotalLowCost(visibleIssues.reduce((sum, issue) => sum + issue.lowcostToRepair, 0));
      setTotalHighCost(visibleIssues.reduce((sum, issue) => sum + issue.highcostToRepair, 0));
    }
  }, [inspectionEvaluation]);

  const fetchSolutionForIssue = async (context: string[], issue: InspectionEvaluationIssue) => {
    setSolutionLoading(true);
    try {
      const stringContext = context;//.map(x => x.quote);
      stringContext.push(issue.longDescription);
      const response = await workAnalyzerService.getInspectionIssueSolutions(stringContext);
      setSelectedSolution(response.Answer);
    } finally {
      setSolutionLoading(false);
    }
  };

  const toggleHidden = (issueId: string) => {
    if (inspectionEvaluation && inspectionEvaluation.issues) {
      const updatedIssues = inspectionEvaluation.issues.map(issue =>
        issue.id === issueId ? { ...issue, hidden: !issue.hidden } : issue
      );
      setInspectionEvaluation({ ...inspectionEvaluation, issues: updatedIssues });
    }
  };

  const handleViewDocument = async () => {
    if (inspectionId) {
      try {
        const documentUrl = await workAnalyzerService.getInspectionDocument(inspectionId);
        window.open(documentUrl, '_blank');
      } catch (error) {
        console.error("Failed to fetch document URL:", error);
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!inspectionEvaluation) {
    return <Typography variant="h6">Inspection data not found.</Typography>;
  }

  const safetyIssues = inspectionEvaluation.issues?.filter(issue => issue.category === 'Safety' && !issue.hidden) || [];
  const repairIssues = inspectionEvaluation.issues?.filter(issue => issue.category === 'Repair' && !issue.hidden) || [];
  const hiddenIssues = inspectionEvaluation.issues?.filter(issue => issue.hidden) || [];
  const applianceCount = inspectionEvaluation.appliances?.length || 0;

  return (
    <Container component="main" maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 4 }}>
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <InspectionEvaluationSummaryDisplay
            inspectionId={inspectionId ?? ""}
            totalLowCost={totalLowCost}
            totalHighCost={totalHighCost}
            issueCount={safetyIssues.length + repairIssues.length}
            score={inspectionEvaluation.score}
            address={inspectionEvaluation.address}
          />
        </Box>

        {solutionLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading solution details...
            </Typography>
          </Box>
        ) : selectedSolution ? (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6">Diagnosis</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{selectedSolution.Diagnosis}</Typography>
            {selectedSolution.Solutions.map((solution: any, solIndex: number) => (
              <Accordion key={solIndex}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{solution.SolutionDescription}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Difficulty: {solution.Difficulty}/10
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>Tools Needed:</Typography>
                  <ul>
                    {solution.Equipment.map((tool: string, toolIndex: number) => (
                      <li key={toolIndex}>{tool}</li>
                    ))}
                  </ul>
                  <Typography variant="body1" sx={{ mt: 2 }}>Steps:</Typography>
                  <ol>
                    {solution.Steps.map((step: string, stepIndex: number) => (
                      <li key={stepIndex}>{step}</li>
                    ))}
                  </ol>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Cost: ${solution.projectedCostLow.toLocaleString()} - ${solution.projectedCostHigh.toLocaleString()}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}

            <Button variant="outlined" color="secondary" fullWidth sx={{ mt: 4 }} onClick={() => setSelectedSolution(null)}>
              Back to Repairs
            </Button>
          </Box>
        ) : (
          <>
            <Accordion disabled={safetyIssues.length === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{
                  color: safetyIssues.length === 0 ? 'rgba(0, 0, 0, 0.4)' : 'inherit', // Lighter disabled color
                }}
              >
                <Typography variant="h6">Safety Issues ({safetyIssues.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {safetyIssues.map(issue => (
                    <InspectionEvaluationIssueDisplay 
                      key={issue.id} 
                      {...issue} 
                      fetchSolution={() => fetchSolutionForIssue(issue.context, issue)} 
                      onToggleHidden={() => toggleHidden(issue.id)}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion disabled={repairIssues.length === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{
                  color: repairIssues.length === 0 ? 'rgba(0, 0, 0, 0.4)' : 'inherit', // Lighter disabled color
                }}
              >
                <Typography variant="h6">Repair Issues ({repairIssues.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {repairIssues.map(issue => (
                    <InspectionEvaluationIssueDisplay 
                      key={issue.id} 
                      {...issue} 
                      fetchSolution={() => fetchSolutionForIssue(issue.context, issue)} 
                      onToggleHidden={() => toggleHidden(issue.id)}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion disabled={applianceCount === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{
                  color: applianceCount === 0 ? 'rgba(0, 0, 0, 0.4)' : 'inherit', // Lighter disabled color
                }}
              >
                <Typography variant="h6">Appliances</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {applianceCount > 0 ? (
                    inspectionEvaluation.appliances.map((appliance, index) => (
                      <InspectionEvaluationApplianceDisplay key={index} appliance={appliance} />
                    ))
                  ) : (
                    <Typography variant="body2">None</Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {hiddenIssues.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Hidden Items ({hiddenIssues.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {hiddenIssues.map(issue => (
                      <InspectionEvaluationIssueDisplay 
                        key={issue.id} 
                        {...issue} 
                        fetchSolution={() => fetchSolutionForIssue(issue.context, issue)} 
                        onToggleHidden={() => toggleHidden(issue.id)}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}

        <Button variant="outlined" color="secondary" fullWidth sx={{ mt: 4 }} onClick={() => navigate('/inspection')}>
          View Inspections
        </Button>
      </Box>
    </Container>
  );
};

export default InspectionEvaluation;
